import { useEffect, useMemo, useState } from 'react'
import type { DriverFilter, DriverSchedule } from '../types'
import dayjs from 'dayjs'
import { useUserSettings } from '@/hooks/settings'
import { getFilterOptions } from '../util'

const DEFAULT_FILTER_OPTIONS: DriverFilter = {
  date: dayjs().toISOString(),
  search_by: 'driver_name',
  driver_names: [],
  depot_names: [],
  order_numbers: [],
  shift: 'All',
  search_term: null,
  type: 'filter', // 'search' or 'filter'
  show_live: false,
}

const FILTERS_SETTINGS_KEY = 'driver-tracking::filters'
const LAST_UPDATED_KEY = 'driver-tracking::last-updated'

export function useDriverFilters(
  allDriverTracking: DriverSchedule[],
  setActiveQueryFilters: React.Dispatch<
    React.SetStateAction<{
      shift_date: string
      order_numbers: number[]
      driver_names: string[]
    }>
  >
) {
  // Initialize selected filters with default values
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTER_OPTIONS)
  const settings = useUserSettings()

  useEffect(() => {
    async function initializeSavedSettings() {
      const savedSettings = (await settings.getSetting(FILTERS_SETTINGS_KEY)) as DriverFilter | null
      const lastUpdatedTime = (await settings.getSetting(LAST_UPDATED_KEY)) as string | null
      if (savedSettings) {
        const prevSettings = { ...selectedFilters, ...savedSettings }
        if (lastUpdatedTime) {
          const lastUpdated = dayjs(lastUpdatedTime)
          if (lastUpdated.isBefore(dayjs().startOf('day'))) {
            // If last updated was more than a day ago, reset date to today
            prevSettings.date = dayjs().toISOString()
          }
        }
        setSelectedFilters(prevSettings)
        setActiveQueryFilters((prev) => ({
          ...prev,
          shift_date: prevSettings.date,
        }))
      }
    }
    initializeSavedSettings()
  }, [])

  const filterOptions = useMemo(() => getFilterOptions(allDriverTracking), [allDriverTracking])

  function handleFilterChange(newFilters: Partial<DriverFilter>) {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }))
    settings.updateSetting(FILTERS_SETTINGS_KEY, newFilters)
    settings.updateSetting(LAST_UPDATED_KEY, dayjs().toISOString())
  }

  function handleDateChange(date: string) {
    handleFilterChange({ date: dayjs(date).toISOString(), search_term: null })

    setActiveQueryFilters((prevFilters) => ({
      ...prevFilters,
      shift_date: dayjs(date).toISOString(),
    }))
  }

  function handleSearch(searchTerm: string | number) {
    if (selectedFilters.type === 'search') {
      handleFilterChange({ search_term: searchTerm })

      if (selectedFilters.search_by === 'driver_name') {
        setActiveQueryFilters((prevFilters) => ({
          ...prevFilters,
          driver_names: [searchTerm as string],
          order_numbers: [],
        }))
      } else {
        setActiveQueryFilters((prevFilters) => ({
          ...prevFilters,
          driver_names: [],
          order_numbers: [Number(searchTerm)],
        }))
      }
    }
  }

  return {
    selectedFilters,
    filterOptions,
    handleFilterChange,
    handleDateChange,
    handleSearch,
  }
}
